<template>
    <div class="services-view">
        <ContentLayout
            :image="heroImage"
            :height="400"
            :mobile-height="320"
            eyebrow="Lavage d'auto"
            icon="mdi-car-side"
            title="Des forfaits clairs pour une voiture propre, dedans comme dehors."
            subtitle="Choisissez le niveau de soin qui convient à votre véhicule, puis réservez en ligne ou appelez-nous."
            lg="10"
            xl="8"
        >
            <template v-slot:hero-actions>
                <v-btn
                    x-large
                    color="secondary"
                    class="primary--text font-weight-bold mr-3 mb-3"
                    :href="bookingUrl"
                    target="_blank"
                    rel="noopener"
                >
                    <v-icon left>mdi-calendar-check</v-icon>
                    Réserver en ligne
                </v-btn>
                <v-btn
                    x-large
                    outlined
                    color="white"
                    class="mr-3 mb-3"
                    :href="telHref"
                >
                    <v-icon left>mdi-phone</v-icon>
                    {{ phone }}
                </v-btn>
                <v-btn
                    x-large
                    outlined
                    color="white"
                    class="mb-3"
                    href="#lavage-auto"
                >
                    <v-icon left>mdi-chevron-down</v-icon>
                    Voir les forfaits
                </v-btn>
            </template>

        <section id="lavage-auto" class="wash-section">
            <v-container>
                <v-row align="end" class="mb-6">
                    <v-col cols="12" md="8">
                        <div class="overline secondary--text font-weight-bold mb-2">Lavage d'auto</div>
                        <h2 class="display-1 font-weight-bold mb-3">Choisissez votre forfait</h2>
                        <p class="body-1 grey--text text--darken-1 mb-0">
                            Trois options simples pour l'entretien régulier, le nettoyage complet ou une remise en beauté plus poussée.
                        </p>
                    </v-col>
                    <v-col cols="12" md="4" class="text-md-right">
                        <v-btn
                            large
                            outlined
                            color="primary"
                            :href="telHref"
                        >
                            <v-icon left>mdi-phone</v-icon>
                            {{ phone }}
                        </v-btn>
                    </v-col>
                </v-row>

                <v-row align="stretch">
                    <v-col
                        v-for="service in washServices"
                        :key="service.name"
                        cols="12"
                        md="4"
                        class="d-flex"
                    >
                        <v-card
                            class="service-card d-flex flex-column"
                            :class="service.cardClass"
                            :dark="service.dark"
                            elevation="10"
                            width="100%"
                        >
                            <div class="service-card__top">
                                <v-chip
                                    v-if="service.badge"
                                    small
                                    label
                                    color="secondary"
                                    text-color="primary"
                                    class="font-weight-bold mb-3"
                                >
                                    {{ service.badge }}
                                </v-chip>
                                <div class="service-name font-weight-bold">{{ service.name }}</div>
                                <div class="service-subtitle font-weight-bold text-uppercase">
                                    {{ service.subtitle }}
                                </div>
                                <div class="service-price secondary--text font-weight-bold">
                                    {{ service.price }}
                                </div>
                            </div>

                            <v-divider :dark="service.dark"></v-divider>

                            <v-card-text class="service-card__body flex-grow-1">
                                <div
                                    v-for="section in service.sections"
                                    :key="section.title"
                                    class="mb-5"
                                >
                                    <div class="service-section-title font-weight-bold mb-2">
                                        {{ section.title }}
                                    </div>
                                    <v-list dense color="transparent" class="pa-0">
                                        <v-list-item
                                            v-for="item in section.items"
                                            :key="item"
                                            class="service-list-item px-0"
                                        >
                                            <v-list-item-icon class="service-list-icon mr-2">
                                                <v-icon small color="secondary">mdi-chevron-right</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content class="py-0">
                                                <v-list-item-title class="service-list-text">
                                                    {{ item }}
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                </div>
                            </v-card-text>

                            <v-card-actions class="service-card__footer pa-4 flex-column align-stretch">
                                <v-btn
                                    block
                                    large
                                    outlined
                                    :color="service.ctaColor"
                                    class="mb-2"
                                    :href="telHref"
                                >
                                    <v-icon left>mdi-phone</v-icon>
                                    Appeler pour réserver
                                </v-btn>
                                <v-btn
                                    block
                                    large
                                    :class="['ml-0', service.ctaClass]"
                                    :color="service.ctaColor"
                                    :href="bookingUrl"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    <v-icon left>mdi-calendar-check</v-icon>
                                    Réserver en ligne
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </section>

        <section class="mechanic-section">
            <v-container>
                <v-row align="center">
                    <v-col cols="12" md="7" lg="8">
                        <v-chip
                            label
                            outlined
                            color="secondary"
                            class="font-weight-bold mb-4"
                        >
                            <v-icon left color="secondary">mdi-tools</v-icon>
                            Mécanique et réparations
                        </v-chip>
                        <h2 class="display-1 font-weight-bold white--text mb-4">
                            Pour les réparations, passez par notre partenaire CDM Écono.
                        </h2>
                        <p class="body-1 mechanic-copy mb-6">
                            Diagnostic, entretien mécanique, réparations et services d'atelier sont pris en charge par Centre de Mécanique Écono.
                        </p>
                        <v-btn
                            x-large
                            color="secondary"
                            class="primary--text font-weight-bold"
                            href="https://cdmecono.com"
                            target="_blank"
                            rel="noopener"
                        >
                            <v-icon left>mdi-open-in-new</v-icon>
                            Visiter cdmecono.com
                        </v-btn>
                    </v-col>
                    <v-col cols="12" md="5" lg="4">
                        <v-card dark color="primary" class="partner-card pa-6" elevation="14">
                            <v-img
                                :src="cdmLogo"
                                contain
                                height="150"
                                class="mb-6"
                            ></v-img>
                            <v-divider dark class="mb-5"></v-divider>
                            <div class="d-flex align-center mb-3">
                                <v-icon color="secondary" class="mr-3">mdi-map-marker</v-icon>
                                <span>{{ $store.state.data.address }}</span>
                            </div>
                            <div class="d-flex align-center">
                                <v-icon color="secondary" class="mr-3">mdi-phone</v-icon>
                                <a class="partner-phone" :href="telHref">{{ phone }}</a>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </section>
        </ContentLayout>
    </div>
</template>

<script>
    import ContentLayout from "./../layouts/Content";

    export default {
        name: "services-view",
        components: {
            ContentLayout
        },
        data: () => ({
            cdmLogo: require("./../../assets/img/partners/cdmecono.svg"),
            washServices: [
                {
                    name: "Rapide",
                    subtitle: "Lavage intérieur et extérieur de base",
                    price: "49,99 $",
                    dark: true,
                    cardClass: "service-card--dark",
                    ctaColor: "secondary",
                    ctaClass: "primary--text font-weight-bold",
                    sections: [
                        {
                            title: "Extérieur",
                            items: [
                                "Lavage à la main",
                                "Nettoyage des jantes et pneus",
                                "Séchage à la microfibre"
                            ]
                        },
                        {
                            title: "Intérieur",
                            items: [
                                "Aspirateur complet de l'habitacle",
                                "Nettoyage des surfaces et panneaux",
                                "Nettoyage des vitres intérieures"
                            ]
                        }
                    ]
                },
                {
                    name: "Silver",
                    subtitle: "Lavage intérieur et extérieur de base rapide",
                    price: "149,99 $",
                    badge: "Le plus demandé",
                    dark: false,
                    cardClass: "service-card--light service-card--featured",
                    ctaColor: "primary",
                    ctaClass: "secondary--text font-weight-bold",
                    sections: [
                        {
                            title: "Extérieur",
                            items: [
                                "Lavage à la main",
                                "Nettoyage des jantes et pneus",
                                "Séchage à la microfibre",
                                "Nettoyage des vitres",
                                "Application de vernis reluisant sur les pneus"
                            ]
                        },
                        {
                            title: "Intérieur",
                            items: [
                                "Aspirateur complet de l'habitacle",
                                "Élimination des particules de sel en surface sur les tapis",
                                "Nettoyage des surfaces et panneaux",
                                "Nettoyage des vitres intérieures et extérieures",
                                "Léger passage sur les bancs",
                                "Désodorisation légère"
                            ]
                        }
                    ]
                },
                {
                    name: "Gold",
                    subtitle: "Lavage intérieur et extérieur complet",
                    price: "299,99 $",
                    dark: true,
                    cardClass: "service-card--dark service-card--gold",
                    ctaColor: "secondary",
                    ctaClass: "primary--text font-weight-bold",
                    sections: [
                        {
                            title: "Extérieur",
                            items: [
                                "Lavage à la main",
                                "Nettoyage des jantes, pneus et passages de roues",
                                "Application de vernis reluisant sur les pneus",
                                "Séchage à la microfibre",
                                "Polissage des lumières avant"
                            ]
                        },
                        {
                            title: "Intérieur",
                            items: [
                                "Aspirateur complet de l'habitacle",
                                "Nettoyage et cirage des vitres intérieures",
                                "Nettoyage des surfaces et panneaux",
                                "Shampooing des tapis et moquettes",
                                "Traitement et hydratation du cuir ou des plastiques",
                                "Désodorisation complète"
                            ]
                        }
                    ]
                }
            ]
        }),
        computed: {
            heroImage() {
                return this.$store.state.data.page_header_img ? 
                    this.$store.state.data.page_header_img : require("./../../assets/img/hero.jpg");
            },
            phone() {
                return this.$store.state.data.phone;
            },
            telHref() {
                return "tel:" + this.phone;
            },
            bookingUrl() {
                return this._env.VUE_APP_BOOKING_URL;
            }
        }
    };
</script>

<style scoped>
    .services-view {
        background: #f5f6f8;
    }

    .wash-section,
    .mechanic-section {
        padding: 64px 0;
    }

    .service-card {
        border-radius: 8px;
        overflow: hidden;
        min-height: 100%;
    }

    .service-card--dark {
        background: #161a1d;
        border: 1px solid rgba(235, 184, 43, 0.45);
    }

    .service-card--light {
        background: #ffffff;
        border: 1px solid rgba(34, 34, 34, 0.08);
    }

    .service-card--featured {
        transform: translateY(-10px);
        border: 2px solid #ebb82b;
    }

    .service-card--gold {
        background: linear-gradient(180deg, #151719 0%, #222222 100%);
    }

    .service-card__top {
        padding: 28px 28px 24px;
    }

    .service-name {
        font-size: 2.25rem;
        line-height: 1;
        text-transform: uppercase;
        letter-spacing: 0;
    }

    .service-subtitle {
        min-height: 44px;
        margin-top: 12px;
        font-size: 0.9rem;
        line-height: 1.25;
    }

    .service-price {
        margin-top: 22px;
        font-size: 3rem;
        line-height: 1;
    }

    .service-card__body {
        padding: 24px 28px 4px;
    }

    .service-section-title {
        font-size: 1.05rem;
        text-transform: uppercase;
        letter-spacing: 0;
    }

    .service-list-item {
        min-height: 30px;
    }

    .service-list-icon {
        align-self: flex-start;
        margin-top: 5px;
        margin-bottom: 0;
    }

    .service-list-text {
        white-space: normal;
        line-height: 1.25;
        font-size: 0.94rem;
    }

    .service-card__footer {
        background: rgba(0, 0, 0, 0.05);
    }

    .mechanic-section {
        background: #1f2933;
    }

    .mechanic-copy {
        color: rgba(255, 255, 255, 0.78);
        max-width: 720px;
        line-height: 1.6;
    }

    .partner-card {
        border-radius: 8px;
        border: 1px solid rgba(235, 184, 43, 0.36);
    }

    .partner-phone {
        color: #ebb82b;
        text-decoration: none;
        font-weight: 700;
    }

    @media (max-width: 959px) {
        .service-card--featured {
            transform: none;
        }
    }

    @media (max-width: 599px) {
        .wash-section,
        .mechanic-section {
            padding: 44px 0;
        }

        .service-card__top,
        .service-card__body {
            padding-left: 22px;
            padding-right: 22px;
        }

        .service-name {
            font-size: 2rem;
        }

        .service-price {
            font-size: 2.55rem;
        }
    }
</style>
